import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";
import Logo from "../assets/Logo.png";
import BookTests from "./BookTests";
import "../styles/Navbar.css";

function scrollToId(id, retries = 10) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (retries > 0) {
    setTimeout(() => scrollToId(id, retries - 1), 80);
  }
}

const navLinks = [
  { label: "Home", route: "/", scrollId: null, sectionId: null },
  { label: "About Us", route: "/about", scrollId: null, sectionId: null },
  { label: "Services", route: "/service", scrollId: null, sectionId: null },
  { label: "Health Packages", route: "/", scrollId: "packages", sectionId: "packages" },
  { label: "Our Team", route: "/about", scrollId: "ourteam", sectionId: "ourteam" },
  { label: "Contact Us", route: "/contact", scrollId: null, sectionId: null },
];

const HOME_SECTIONS = [
  "home",
  "services",
  "popular-tests",
  "packages",
  "why-choose",
];

/* Derive { route: [sectionId, sectionId, ...] } for every non-home
   route that has section-scoped nav links (e.g. "/about" → Doctors
   pointing at "ourteam"). This lets links that share the same route
   (like "About Us" and "Doctors") be scroll-spied independently
   instead of both lighting up together. "/" keeps using the fixed
   HOME_SECTIONS list above so its existing behaviour is untouched. */
const ROUTE_SECTION_IDS = navLinks.reduce((acc, link) => {
  if (link.sectionId && link.route !== "/") {
    if (!acc[link.route]) acc[link.route] = [];
    if (!acc[link.route].includes(link.sectionId)) {
      acc[link.route].push(link.sectionId);
    }
  }
  return acc;
}, {});

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [showBookTests, setShowBookTests] = useState(false);

  /* shadow on scroll */
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  /* close mobile menu on route change */
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /* track visible section for the current route (home page uses
     HOME_SECTIONS, any other route uses its own section ids from
     ROUTE_SECTION_IDS if it has any) */
  useEffect(() => {
    const sectionIds =
      location.pathname === "/"
        ? HOME_SECTIONS
        : ROUTE_SECTION_IDS[location.pathname] || [];

    setActiveSection(null);

    if (sectionIds.length === 0) return;

    const observers = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        },
        { threshold: 0.3 }
      );

      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [location.pathname]);

  function handleNav(route, scrollId, closeMobile = false) {
    if (closeMobile) setMenuOpen(false);

    if (location.pathname === route) {
      scrollId
        ? scrollToId(scrollId)
        : window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(route);

      setTimeout(() => {
        scrollId
          ? scrollToId(scrollId)
          : window.scrollTo({ top: 0, behavior: "smooth" });
      }, 120);
    }
  }

  function isActive(link) {
    if (location.pathname !== link.route && link.route === "/") {
      return false;
    }

    if (link.route === "/") {
      if (!link.sectionId) return false;
      return activeSection === link.sectionId;
    }

    const pathMatches = location.pathname.startsWith(link.route);
    if (!pathMatches) return false;

    const routeSectionIds = ROUTE_SECTION_IDS[link.route];
    if (routeSectionIds && routeSectionIds.length > 0) {
      if (link.sectionId) {
        return activeSection === link.sectionId;
      }
      // Plain link sharing this route (e.g. "About Us") — active
      // only while none of the route's specific sections are in view
      return !routeSectionIds.includes(activeSection);
    }

    return true;
  }

  return (
    <header className={`nb-header${scrolled ? " nb-header--scrolled" : ""}`}>
      <Container fluid="xxl" className="nb-container">

        <button
          className="nb-logo"
          onClick={() => handleNav("/", null)}
          aria-label="Go to home"
        >
          <img
            src={Logo}
            alt="TANBIO Biomed Genomics Laboratory"
            loading="lazy"
          />
        </button>

        {/* Desktop Nav */}
        <nav className="nb-nav">
          {navLinks.map((link) => (
            <button
              key={link.label}
              className={`nb-link nb-link--btn${
                isActive(link) ? " nb-link--active" : ""
              }`}
              onClick={() => handleNav(link.route, link.scrollId)}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="nb-actions">
          <a href="tel:7358798182" className="nb-phone">
            <div className="nb-phone-icon">
              <i className="fa-solid fa-phone"></i>
            </div>

            <div className="nb-phone-text">
              <span className="nb-phone-number">
                73587 98182
              </span>
              <span className="nb-phone-label">
                Call Us Now
              </span>
            </div>
          </a>

          <button
            className="nb-book-btn"
            onClick={() => setShowBookTests(true)}
          >
            <i className="fa-regular fa-calendar-check"></i>
            Book a Test
          </button>
        </div>

        {/* Hamburger */}
        <button
          className={`nb-hamburger${
            menuOpen ? " nb-hamburger--open" : ""
          }`}
          onClick={() => setMenuOpen((p) => !p)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </Container>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="nb-mobile-menu">

          {navLinks.map((link) => (
            <button
              key={link.label}
              className={`nb-mobile-link${
                isActive(link)
                  ? " nb-mobile-link--active"
                  : ""
              }`}
              onClick={() =>
                handleNav(
                  link.route,
                  link.scrollId,
                  true
                )
              }
            >
              {link.label}
            </button>
          ))}

          <div className="nb-mobile-divider"></div>

          <a
            href="tel:7358798182"
            className="nb-mobile-link nb-mobile-phone"
          >
            <i className="fa-solid fa-phone"></i>
            {" "}73587 98182
          </a>

          <div className="nb-mobile-book-wrap">
            <button
              className="nb-book-btn"
              onClick={() => {
                setMenuOpen(false);
                setShowBookTests(true);
              }}
            >
              <i className="fa-regular fa-calendar-check"></i>
              {" "}Book a Test
            </button>
          </div>

        </div>
      )}

      <BookTests show={showBookTests} onClose={() => setShowBookTests(false)} />
    </header>
  );
}